import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useUserContext } from '../context/UserContext';
import { MeDto } from '../context/UserContext';

const FONT: React.CSSProperties = { fontFamily: "'Montserrat', sans-serif" };
const MAX_POLLS = 10;
const POLL_INTERVAL = 3000;

export function CheckoutSuccessPage() {
  const { authFetch } = useApi();
  const { refresh } = useUserContext();
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [me, setMe] = useState<MeDto | null>(null);
  const [orgName, setOrgName] = useState('');
  const [savingOrgName, setSavingOrgName] = useState(false);
  const [orgNameSaved, setOrgNameSaved] = useState(false);
  const pollCount = useRef(0);

  useEffect(() => {
    const interval = setInterval(async () => {
      pollCount.current += 1;
      try {
        const res = await authFetch('/api/me');
        const data: MeDto = await res.json();
        if (data.hasPaidSubscription) {
          clearInterval(interval);
          setMe(data);
          refresh();
          setConfirmed(true);
        } else if (pollCount.current >= MAX_POLLS) {
          clearInterval(interval);
          setTimedOut(true);
        }
      } catch {
        if (pollCount.current >= MAX_POLLS) {
          clearInterval(interval);
          setTimedOut(true);
        }
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const needsOrgSetup = !!(me?.isOrgManager && me.organization && me.organization.name === '' && !orgNameSaved);
  const emailDomain = me?.email.includes('@') ? me.email.split('@')[1] : null;

  const handleSaveOrgName = async () => {
    if (!orgName.trim()) return;
    setSavingOrgName(true);
    try {
      const res = await authFetch('/api/me/organization', {
        method: 'PUT',
        body: JSON.stringify({ name: orgName.trim() }),
      });
      if (res.ok) {
        setOrgNameSaved(true);
        refresh();
      }
    } finally {
      setSavingOrgName(false);
    }
  };

  const pending = !confirmed && !timedOut;

  const ctaButton = (
    <button
      onClick={() => navigate('/dashboard')}
      style={{
        ...FONT,
        background: '#958945',
        color: '#fff',
        border: 'none',
        borderRadius: 4,
        padding: '0.75rem 2.25rem',
        fontSize: 16,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      Go to Dashboard
    </button>
  );

  return (
    <div style={{ ...FONT, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAF8' }}>
      <div style={{ maxWidth: 520, width: '100%', padding: '3rem 2rem', textAlign: 'center' }}>

        {pending && (
          <>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              border: '4px solid #E0DEDA',
              borderTopColor: '#ba9394',
              animation: 'spin 0.75s linear infinite',
              margin: '0 auto 2rem',
            }} />
            <h1 style={{ fontSize: 30, fontWeight: 500, margin: '0 0 0.75rem', color: '#000' }}>
              Confirming your subscription…
            </h1>
            <p style={{ fontSize: 16, color: '#66635F', margin: 0 }}>
              Verifying your payment with Stripe.
            </p>
          </>
        )}

        {confirmed && needsOrgSetup && (
          <>
            <div style={{ fontSize: 56, lineHeight: 1, marginBottom: '1.5rem', color: '#958945' }}>✓</div>
            <h1 style={{ fontSize: 30, fontWeight: 500, margin: '0 0 0.75rem', color: '#000' }}>
              Name Your Organization
            </h1>
            <p style={{ fontSize: 16, color: '#66635F', margin: '0 0 1.5rem' }}>
              You're the Manager for Prism accounts using an{emailDomain ? ` @${emailDomain}` : ''} email address.
              Give your organization a name to finish setup.
            </p>
            <input
              type="text"
              value={orgName}
              onChange={e => setOrgName(e.target.value)}
              placeholder="e.g. Acme Realty"
              style={{
                ...FONT, width: '100%', maxWidth: 360, padding: '0.65rem 0.85rem', fontSize: 15,
                border: '1px solid #ddd', borderRadius: 4, marginBottom: '1.25rem', boxSizing: 'border-box',
              }}
            />
            <div>
              <button
                onClick={handleSaveOrgName}
                disabled={savingOrgName || !orgName.trim()}
                style={{
                  ...FONT, background: '#958945', color: '#fff', border: 'none', borderRadius: 4,
                  padding: '0.75rem 2.25rem', fontSize: 16, fontWeight: 600,
                  cursor: savingOrgName || !orgName.trim() ? 'default' : 'pointer',
                  opacity: savingOrgName || !orgName.trim() ? 0.6 : 1,
                }}
              >
                {savingOrgName ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}

        {confirmed && !needsOrgSetup && (
          <>
            <div style={{ fontSize: 56, lineHeight: 1, marginBottom: '1.5rem', color: '#958945' }}>✓</div>
            <h1 style={{ fontSize: 30, fontWeight: 500, margin: '0 0 0.75rem', color: '#000' }}>
              You're subscribed!
            </h1>
            <p style={{ fontSize: 16, color: '#66635F', margin: me?.isOrgManager && me.organization?.isApproved === false ? '0 0 1rem' : '0 0 2rem' }}>
              Your subscription is active. Welcome to Prism.
            </p>
            {me?.isOrgManager && me.organization?.isApproved === false && (
              <p style={{ fontSize: 14, color: '#66635F', margin: '0 0 2rem', maxWidth: 400 }}>
                Your organization is pending review. You have full access to your purchased market; teammates
                will be able to join automatically once it's approved.
              </p>
            )}
            {ctaButton}
          </>
        )}

        {timedOut && (
          <>
            <div style={{ fontSize: 56, lineHeight: 1, marginBottom: '1.5rem', color: '#958945' }}>✓</div>
            <h1 style={{ fontSize: 30, fontWeight: 500, margin: '0 0 0.75rem', color: '#000' }}>
              Payment received
            </h1>
            <p style={{ fontSize: 16, color: '#66635F', margin: '0 0 2rem' }}>
              Your access may take a moment to activate. If your dashboard isn't unlocked yet, try refreshing in a minute.
            </p>
            {ctaButton}
          </>
        )}

      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
